import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { audioBus } from '../../core/AudioBus.js';
import { WalkingDog } from '../town/WalkingDog.js';

export class SplashScene extends BaseScene {
  static readonly KEY = 'Splash';

  private dog: WalkingDog | null = null;
  private heartTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super(SplashScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_town');
  }

  create(): void {
    const cx = this.gameW / 2;
    const cy = this.gameH / 2;

    // pełne ilustrowane tło (fallback: różowy)
    this.cameras.main.setBackgroundColor(0xffe4ee);
    const bg = this.addRealBg('bg_town', 0);
    if (bg) bg.setAlpha(0.9);
    // miękka poświata rozjaśniająca środek pod kartę
    this.add.rectangle(cx, cy, this.gameW, this.gameH, 0xffffff, 0.12).setDepth(1);

    // karta powitalna
    const card = this.add
      .rectangle(cx, cy + 20, 600, 400, 0xfff8f0, 0.9)
      .setStrokeStyle(6, 0xffb6c1)
      .setDepth(10);
    card.setScale(0.85);
    this.tweens.add({
      targets: card,
      scale: 1,
      duration: 420,
      ease: 'Back.Out',
    });

    // animowana Missi nad tytułem
    if (this.textures.exists('dog_maltese_idle') || this.textures.exists('ph_dog')) {
      this.dog = new WalkingDog(this, { breed: 'maltese', size: 130 });
      this.dog.image.setPosition(cx, cy - 120).setDepth(20);
      this.tweens.add({
        targets: this.dog.image,
        angle: { from: -4, to: 4 },
        duration: 1400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.InOut',
      });
    }

    // tytuł z pop-inem
    const title = this.add
      .text(cx, cy - 20, 'Pieskowo', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '58px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(20);
    title.setScale(0.5);
    title.setAlpha(0);
    this.tweens.add({
      targets: title,
      scale: 1,
      alpha: 1,
      duration: 520,
      delay: 160,
      ease: 'Back.Out',
    });

    this.add
      .text(cx, cy + 34, 'Gra Misi i Loli', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        color: '#6b5f7a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(20);
    this.add
      .text(cx, cy + 70, 'Witaj w miasteczku piesków! 🐾', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setDepth(20);

    // przycisk „Zaczynamy!" z delikatnym pulsem
    const btn = UiKit.button(this, {
      x: cx,
      y: cy + 140,
      label: '▶  Zaczynamy!',
      fill: 0xb8e6d0,
      onClick: () => {
        audioBus.unlock();
        getSceneRouter().go('Onboarding');
      },
    });
    btn.setDepth(20);
    this.tweens.add({
      targets: btn,
      scaleX: 1.06,
      scaleY: 1.06,
      duration: 780,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });

    // unoszące się serduszka/łapki — świat żyje od pierwszego ekranu
    this.heartTimer = this.time.addEvent({
      delay: 850,
      loop: true,
      callback: () => this.spawnFloater(),
    });

    this.events.once('shutdown', () => this.teardown());
    this.events.once('destroy', () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    this.dog?.update(Math.min(0.05, dtMs / 1000), false, 0);
  }

  private spawnFloater(): void {
    const emoji = Math.random() < 0.5 ? '💗' : '🐾';
    const x = 120 + Math.random() * (this.gameW - 240);
    const f = this.add
      .text(x, this.gameH - 40, emoji, { fontSize: '30px' })
      .setOrigin(0.5)
      .setDepth(5)
      .setAlpha(0.9);
    this.tweens.add({
      targets: f,
      y: this.gameH * 0.35,
      alpha: 0,
      duration: 3200,
      ease: 'Sine.Out',
      onComplete: () => f.destroy(),
    });
  }

  private teardown(): void {
    this.heartTimer?.remove();
    this.heartTimer = undefined;
    this.dog?.destroy();
    this.dog = null;
  }
}
